/**
 * Created by mzhao on 07/05/2022.
 * Description:
 * ContactList Service
 * ------ maintenance history ------
 *  Updated by Marcus Zhao on 12/8/2022: support more columns and display data for contact list.
 *                                       support group by cloumns.
 *                                       support show refresh contact list.
 */
import { Injectable } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Subject } from 'rxjs';
import { ResearchView } from '../../constants/business.constants';
import { DEFAULT_PAGE_SIZE } from '../../widgets/grid/grid.model';
import { ContactListBulkEditComponent } from './contact-list-bulk-edit/contact-list-bulk-edit.component';
import { CustomActionColumnComponent } from '../../widgets/ag-grid/custom-action-column.component';

@Injectable({
    providedIn: 'root'
})
export class ContactListService {
    constructor() {

    }
    feedbackSubject$?: Subject<any> = new Subject<any>();
    gridFeedbackSubject$?: Subject<any> = new Subject<any>();

    columnDefs: Array<ColDef> =
        [
            {
                headerName: 'Sync',
                cellStyle: (params) => {
                    if (params.data && params.data.suspend) {
                        return {
                            'background': 'url("./assets/images/singlesvg/disconnected.svg")',
                            'background-position': 'center',
                            'background-repeat': 'no-repeat'
                        };
                    } else {
                        if (params.data && params.data.syncType) {
                            if (params.data.syncType === 1) {
                                return {
                                    'background': 'url("./assets/images/singlesvg/oneWayLinked.svg")',
                                    'background-position': 'center',
                                    'background-repeat': 'no-repeat'
                                };
                            } else if (params.data.syncType === 2) {
                                return {
                                    'background': 'url("./assets/images/singlesvg/synch.svg")',
                                    'background-position': 'center',
                                    'background-repeat': 'no-repeat'
                                };
                            }
                        }
                    }
                },
                minWidth: 70,
                width: 70,
                maxWidth: 70,
                colId: 'exchangeStatus',
                enableRowGroup: false,
                tooltipValueGetter: this._tooltip,
                menuTabs: [],
            },
            {
                headerName: 'Full Name',
                valueGetter: (params) => {
                    let name = '';
                    if (params.data && params.data.attribute && params.data.attribute.prefix) {
                        name = name + ' ' + params.data.attribute.prefix;
                    }
                    if (params.data && params.data.firstName) {
                        name = name + ' ' + params.data.firstName;
                    }
                    if (params.data && params.data.attribute && params.data.attribute.middleName) {
                        name = name + ' ' + params.data.attribute.middleName;
                    }
                    if (params.data && params.data.lastName) {
                        name = name + ' ' + params.data.lastName;
                    }
                    if (params.data && params.data.attribute && params.data.attribute.suffix) {
                        name = name + ' ' + params.data.attribute.suffix;
                    }
                    return name.trim();
                },
                tooltipValueGetter: this._tooltip,
                colId: 'full-name',
                enableRowGroup: false,
            },
            {
                headerName: 'First Name',
                valueGetter: 'data.firstName',
                tooltipValueGetter: this._tooltip,
                enableRowGroup: false,
                colId: 'first-name',
            },
            {
                headerName: 'Last Name',
                valueGetter: 'data.lastName',
                tooltipValueGetter: this._tooltip,
                colId: 'last-name',
                enableRowGroup: false,
            },
            {
                headerName: 'Job Title',
                valueGetter: 'data.jobTitleWeb.filter(item => item.id).map((res=>res.name))',
                tooltipValueGetter: this._tooltip,
                colId: 'job-title',
                enableRowGroup: false,
            },
            {
                headerName: 'Company',
                valueGetter: (params) => {
                    let company = '';
                    if (params.data && params.data.companyWeb && params.data.companyWeb.name) {
                        if (params.data.companyWeb.id) {
                            company = params.data.companyWeb.name;
                        } else {
                            company = `${params.data.companyWeb.name} *`;
                        }
                    }
                    return company;
                },
                tooltipValueGetter: this._tooltip,
                colId: 'company',
                enableRowGroup: false,
            },
            {
                headerName: 'Primary Email',
                valueGetter: (params) => {
                    let primaryEmail = '';
                    if (params.data && params.data.primaryEmail) {
                        primaryEmail = params.data.primaryEmail;
                        if (primaryEmail === '{}') {
                            primaryEmail = '';
                        }
                    }
                    return primaryEmail;
                },
                tooltipValueGetter: this._tooltip,
                colId: 'primary-email',
                enableRowGroup: false,
            },

            {
                headerName: 'Job Function',
                valueGetter: (params) => {
                    let jobFunction = '';
                    if (params.data && params.data.jobFunction && params.data.jobFunction.length > 0) {
                        jobFunction = params.data.jobFunction.map(element => element.name);
                    }
                    return jobFunction;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'job-function',
                enableRowGroup: false,
            },
            {
                headerName: 'Alias',
                valueGetter: (params) => {
                    let alias = '';
                    if (params.data && params.data.aliases && params.data.aliases.length > 0) {
                        alias = params.data.aliases;
                    }
                    return alias;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'alias',
                enableRowGroup: false,
            },
            {
                headerName: 'Prefix',
                valueGetter: (params) => {
                    let prefix = '';
                    if (params.data && params.data.attribute && params.data.attribute.prefix) {
                        prefix = params.data.attribute.prefix;
                    }
                    return prefix;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'prefix',
                enableRowGroup: false,
            },
            {
                headerName: 'Middle Name',
                valueGetter: (params) => {
                    let middleName = '';
                    if (params.data && params.data.attribute && params.data.attribute.middleName) {
                        middleName = params.data.attribute.middleName;
                    }
                    return middleName;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'middleName',
                enableRowGroup: false,
            },
            {
                headerName: 'Gender',
                valueGetter: (params) => {
                    let gender = '';
                    if (params.data && params.data.attribute && params.data.attribute.gender) {
                        gender = params.data.attribute.gender;
                    }
                    return gender;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'gender',
                enableRowGroup: false,
            },
            {
                headerName: 'Suffix',
                valueGetter: (params) => {
                    let suffix = '';
                    if (params.data && params.data.attribute && params.data.attribute.suffix) {
                        suffix = params.data.attribute.suffix;
                    }
                    return suffix;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'suffix',
                enableRowGroup: false,
            },
            {
                headerName: 'Birthday',
                valueGetter: (params) => {
                    let birthday = '';
                    if (params.data && params.data.birthday) {
                        birthday = params.data.birthday;
                    }
                    return birthday;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'birthday',
                enableRowGroup: false,
            },
            {
                headerName: 'Work Email',
                valueGetter: (params) => {
                    let workEmail = '';
                    if (params.data && params.data.webEmail) {
                        workEmail = params.data.webEmail.filter(element => element.name === 'Work').map((res => res.address));
                    }
                    return workEmail;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'work-email',
                enableRowGroup: false,
            },
            {
                headerName: 'Personal Email',
                valueGetter: (params) => {
                    let personalEmail = '';
                    if (params.data && params.data.webEmail) {
                        personalEmail = params.data.webEmail.filter(element => element.name === 'Personal').map((res => res.address));
                    }
                    return personalEmail;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'personal-email',
                enableRowGroup: false,
            },

            {
                headerName: 'Other Email',
                valueGetter: (params) => {
                    let otherEmail = '';
                    if (params.data && params.data.webEmail) {
                        otherEmail = params.data.webEmail.filter(element => element.name === 'Other').map((res => res.address));
                    }
                    return otherEmail;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'other-email',
                enableRowGroup: false,
            },
            {
                headerName: 'Assistant Email',
                valueGetter: (params) => {
                    let assistantEmail = '';
                    if (params.data && params.data.webEmail) {
                        assistantEmail = params.data.webEmail.filter(element => element.name === 'Assistant').map((res => res.address));
                    }
                    return assistantEmail;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'assistant-email',
                enableRowGroup: false,
            },
            {
                headerName: 'Work Phone',
                valueGetter: (params) => {
                    let workphone = '';
                    if (params.data && params.data.webPhone) {
                        workphone = params.data.webPhone.filter(element => element.name === 'Work').map((res => res.phone));
                    }
                    return workphone;
                },
                tooltipValueGetter: this._tooltip,
                colId: 'work-phone',
                enableRowGroup: false,
            },

            {
                headerName: 'Home Phone',
                valueGetter: (params) => {
                    let homePhone = '';
                    if (params.data && params.data.webPhone) {
                        homePhone = params.data.webPhone.filter(element => element.name === 'Home').map((res => res.phone));
                    }
                    return homePhone;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'home-phone',
                enableRowGroup: false,
            },
            {
                headerName: 'Mobile Phone',
                valueGetter: (params) => {
                    let mobilePhone = '';
                    if (params.data && params.data.webPhone) {
                        mobilePhone = params.data.webPhone.filter(element => element.name === 'Mobile').map((res => res.phone));
                    }
                    return mobilePhone;
                },
                tooltipValueGetter: this._tooltip,
                colId: 'mobile-phone',
                enableRowGroup: false,
            },
            {
                headerName: 'Fax',
                valueGetter: (params) => {
                    let faxPhone = '';
                    if (params.data && params.data.webPhone) {
                        faxPhone = params.data.webPhone.filter(element => element.name === 'Fax').map((res => res.phone));
                    }
                    return faxPhone;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'fax',
                enableRowGroup: false,
            },
            {
                headerName: 'Work Address',
                valueGetter: (params) => {
                    let workAddress = '';
                    if (params.data && params.data.webAddress) {
                        workAddress = params.data.webAddress.filter(element => element.name === 'Work').map((webAddressItem => {
                            const webAddressValue = [webAddressItem.street2, webAddressItem.street, webAddressItem.city, webAddressItem.state, webAddressItem.country, webAddressItem.zip].filter(item => !!item).join(', ');
                            return webAddressValue;
                        }));
                    }
                    return workAddress;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'work-address',
                enableRowGroup: false,
            },
            {
                headerName: 'Home Address',
                valueGetter: (params) => {
                    let homeAddress = '';
                    if (params.data && params.data.webAddress) {
                        homeAddress = params.data.webAddress.filter(element => element.name === 'Home').map((webAddressItem => {
                            const webAddressValue = [webAddressItem.street2, webAddressItem.street, webAddressItem.city, webAddressItem.state, webAddressItem.country, webAddressItem.zip].filter(item => !!item).join(', ');
                            return webAddressValue;
                        }));
                    }
                    return homeAddress;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'home-address',
                enableRowGroup: false,
            },
            {
                headerName: 'Linkedin',
                valueGetter: (params) => {
                    let linkedin = '';
                    if (params.data && params.data.socialMedia) {
                        linkedin = params.data.socialMedia.filter(element => element.name === 'LinkedIn').map((res => res.link));
                    }
                    return linkedin;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'linkedin',
                enableRowGroup: false,
            },
            {
                headerName: 'IM',
                valueGetter: (params) => {
                    let IM = '';
                    if (params.data && params.data.socialMedia) {
                        IM = params.data.socialMedia.filter(element => element.name === 'IM').map((res => res.link));
                    }
                    return IM;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'IM',
            },
            {
                headerName: 'Facebook',
                valueGetter: (params) => {
                    let facebook = '';
                    if (params.data && params.data.socialMedia) {
                        facebook = params.data.socialMedia.filter(element => element.name === 'Facebook').map((res => res.link));
                    }
                    return facebook;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'facebook',
            },
            {
                headerName: 'Twitter',
                valueGetter: (params) => {
                    let twitter = '';
                    if (params.data && params.data.socialMedia) {
                        twitter = params.data.socialMedia.filter(element => element.name === 'Twitter').map((res => res.link));
                    }
                    return twitter;
                },
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'twitter',
                enableRowGroup: false,
            },
            {
                headerName: 'Owner',
                valueGetter: 'data.ownerName',
                hide: true,
                tooltipValueGetter: this._tooltip,
                colId: 'owner',
                enableRowGroup: false,
            }
        ];

    checkColumnDef: ColDef =
        {
            headerName: '',
            // cellStyle: (params) => {

            // },
            minWidth: 60,
            width: 60,
            // checkboxSelection: true,
            // headerCheckboxSelection: true,

            // maxWidth: 40,
            // valueGetter: 'data.mobilePhone',
            colId: 'checkbox',
            tooltipValueGetter: this._tooltip,
            cellRenderer: ContactListBulkEditComponent,
            cellRendererParams: {
                feedbackSubject$: () => this.feedbackSubject$,
                // feedbackType:
                type: 'radio-button'
            },
            menuTabs: [],
        };

    deleteColumnDef: ColDef =
        {
            headerName: 'Delete',
            minWidth: 60,
            width: 60,
            colId: 'delete',
            cellRenderer: CustomActionColumnComponent,
            cellRendererParams: {
                feedbackSubject$: () => this.gridFeedbackSubject$
            },
            menuTabs: [],
        };

    ownerColumnDef: ColDef =
        {
            headerName: 'Owner',
            valueGetter: 'data.ownerName',
            tooltipValueGetter: this._tooltip,
            colId: 'owner',
            // minWidth: 40,
            // width: 40,
            // maxWidth: 40,
        };

    gridOptions: GridOptions = {
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 150,
            maxWidth: 300,
        },
        cacheBlockSize: ResearchView.cacheBlockSize,
        suppressCellFocus: true,
        suppressColumnVirtualisation: true,
        enableBrowserTooltips: true,
        paginationPageSize: DEFAULT_PAGE_SIZE,
        colResizeDefault: 'shift',
        rowSelection: 'single', // we only need to support single selection on contat list.
        rowGroupPanelShow: 'always',
        sortingOrder: ['desc', 'asc'],
        maintainColumnOrder: true,
        suppressContextMenu: true,
        suppressDragLeaveHidesColumns: true,
        suppressMakeColumnVisibleAfterUnGroup: true,
        suppressRowGroupHidesColumns:true,
        suppressRowTransform: true,
        overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>',
    };

    compareValue(columnDefs) {
        columnDefs.forEach(def => {
            def.comparator = function (valueA, valueB, nodeA, nodeB) {
                // here valueA and valueB are the items to be compared
                if (valueA && valueB && valueA.length > 0 && valueB.length > 0) {
                    if (valueA instanceof Array) {
                        valueA = valueA[0];
                    }
                    if (valueB instanceof Array) {
                        valueB = valueB[0];
                    }
                    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
                }
            };
        });
    }

    private _tooltip(params) {
        return (params.value);
    }
}
